import { useCallback, useEffect } from 'react';

import { usePaymentContext } from 'features/payment';

import { isCreditCardPaymentMethod } from 'components/payment-method-option/utils';
import { CartPaymentCardType } from 'generated/graphql-gateway';
import { usePrepaidsPaymentMethods as usePrepaidMethods } from 'hooks/prepaid/use-prepaids-payment-methods';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { ServiceMode } from 'state/service-mode/types';

import {
  PaymentMethodSelector,
  PaymentMethodSelectorProps,
} from '../../../payment/components/payment-method-selector';
import { SelectionType } from '../../../payment/components/payment-method-selector/types';
import { useCheckoutContext } from '../../hooks/use-checkout-context';
import { useCheckoutLoadingState } from '../../hooks/use-checkout-loading-state';

const MODAL_OPEN_DELAY = 300;
export const PaymentSelector = () => {
  const {
    noPaymentMethodsAvailable,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
    setPrepaidReloadPaymentMethodId,
    setDefaultReloadPaymentMethodId,
    setIsAddCreditCardModalVisible,
    setIsAddReloadPrepaidMethodModalVisible,
    setIsAddUpdateCardForReloadModalVisible,
    setAccountToDelete: setAccountToUpdate,
    getBalanceFromPaymentMethods,
  } = usePaymentContext();

  const {
    isSubmittingOrder,
    isLoadingPaymentMethods,
    isCheckoutLoading,
  } = useCheckoutLoadingState();

  // Wondering if we should pass this serviceMode as a prop instead of using the context
  const {
    showReloadPaymentMethods,
    reloadAmount,
    serviceMode,
    paymentMethods,
    checkoutPaymentMethodId,
    checkoutPaymentMethod,
    serverOrder,
  } = useCheckoutContext();
  const { reloadPaymentMethods, selectedReloadPaymentMethod } = usePrepaidMethods();

  const handleSelectPaymentMethod = useCallback(
    (type: SelectionType, id?: string | undefined) => {
      switch (type) {
        case 'add':
          setAccountToUpdate('');
          setTimeout(() => {
            if (id?.includes('reload-')) {
              setIsAddUpdateCardForReloadModalVisible(true);
            } else {
              setIsAddCreditCardModalVisible(true);
            }
          }, MODAL_OPEN_DELAY);
          break;

        case 'update':
          if (id?.includes('reload-')) {
            const sanitizedId = (id || '').replace('reload-', '');
            setAccountToUpdate(sanitizedId);
          } else {
            setAccountToUpdate(id || '');
            setTimeout(() => {
              setIsAddCreditCardModalVisible(true);
            }, MODAL_OPEN_DELAY);
          }
          break;

        case 'prepaid':
          setTimeout(() => {
            setIsAddReloadPrepaidMethodModalVisible(true);
          }, MODAL_OPEN_DELAY);
          break;

        case 'selection':
          if (showReloadPaymentMethods && id?.includes('prepaid-')) {
            const sanitizedId = (id || '').replace('prepaid-', '');
            setPrepaidReloadPaymentMethodId(sanitizedId);
            setDefaultReloadPaymentMethodId(sanitizedId);
          } else {
            setDefaultPaymentMethodId(id ?? '');
            if (!id) {
              setCheckoutPaymentMethodId('');
              return;
            }
            setCheckoutPaymentMethodId(id);
          }
          break;
      }
    },
    [
      setAccountToUpdate,
      showReloadPaymentMethods,
      setIsAddUpdateCardForReloadModalVisible,
      setIsAddCreditCardModalVisible,
      setIsAddReloadPrepaidMethodModalVisible,
      setPrepaidReloadPaymentMethodId,
      setDefaultReloadPaymentMethodId,
      setDefaultPaymentMethodId,
      setCheckoutPaymentMethodId,
    ]
  );

  const paymentMethodSelectorProps: PaymentMethodSelectorProps = {
    isLoading: !isSubmittingOrder && isLoadingPaymentMethods,
    methods: paymentMethods,
    selectedMethodId: checkoutPaymentMethodId ?? undefined,
    serviceMode: serviceMode ?? ServiceMode.DRIVE_THRU,
    noPaymentMethodsAvailable,
    isDisabled: isCheckoutLoading,
    prepaid: {
      shouldReloadPrepaidMethod: showReloadPaymentMethods,
      reloadAmount,
      allowedMethodsForReload: reloadPaymentMethods,
      selectedMethodId:
        (selectedReloadPaymentMethod?.accountIdentifier ||
          selectedReloadPaymentMethod?.fdAccountId) ??
        undefined,
      prepaidBalance: checkoutPaymentMethod
        ? getBalanceFromPaymentMethods(checkoutPaymentMethod)
        : 0,
      orderTotal: serverOrder?.cart?.totalCents ?? 0,
    },
    onSelect: handleSelectPaymentMethod,
  };

  useEffect(() => {
    if (checkoutPaymentMethod) {
      let paymentInfo: { paymentMethod: string; cardBrand?: string };
      const isCreditCard = isCreditCardPaymentMethod(checkoutPaymentMethod);
      if (isCreditCard && checkoutPaymentMethod.credit) {
        paymentInfo = {
          paymentMethod: CartPaymentCardType.CREDIT,
          cardBrand: checkoutPaymentMethod.credit.cardType,
        };
      } else if (checkoutPaymentMethod.prepaid) {
        paymentInfo = {
          paymentMethod: CartPaymentCardType.GIFT_CARD,
        };
      } else {
        paymentInfo = {
          paymentMethod: checkoutPaymentMethod.accountIdentifier || '',
        };
      }

      logRBIEvent(
        {
          name: CustomEventNames.PAYMENT_METHOD_SELECTED,
          type: EventTypes.Other,
          attributes: paymentInfo,
        },
        { skipLoggingToBraze: true }
      );
    }
  }, [checkoutPaymentMethod]);
  return <PaymentMethodSelector {...paymentMethodSelectorProps} />;
};
